import React from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import { useI18n } from '@/i18n/I18nProvider';
import { useGetStudentById } from '@/lib/hooks/useGetStudentById';

import { Card } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Breadcrumb } from '@/components/ui/breadcrumb';

import StudentActions from '@/components/StudentActions';
import InfoTab from './components/InfoTab';
import StudentProfileCard from './components/StudentProfileCard';
import Spinner from '@/components/ui/spinner';
import RefreshBar from '@/components/RefreshBar';
import { FileTextIcon, WarningCircleIcon } from '@/components/icons';
import { StatusCard } from '@/components/StatusCard';
import { resolveLatestEnrollment } from '@/lib/helpers';

export default function ChildDetailPage() {
  const { t, locale } = useI18n();
  const params = useParams<{ id: string }>();
  const sourcedId = params.id as string;

  // Fetch student data from PP API
  const { student, error, isLoading } = useGetStudentById(sourcedId);

  if (isLoading) {
    return <Spinner />;
  }
  if (error || !student) {
    return <StatusCard message={error?.title} description={t.child.no_data_available_for_child} />;
  }

  const displayName =
    locale === 'ar'
      ? [student.firstNameArabic, student.middleNameArabic, student.lastNameArabic].filter(Boolean).join(' ')
      : [
          student.firstNameEnglish,
          student.middleNameEnglish,
          student.thirdNameEnglish,
          student.fourthNameEnglish,
          student.familyNameEnglish,
        ]
          .filter(Boolean)
          .join(' ');

  const latestEnrollment = resolveLatestEnrollment(student.enrollment);
  const educationType = latestEnrollment?.educationType ?? null;
  const isPrivateEducation = educationType?.toLowerCase() === 'private';

  return (
    <div
      className={clsx(
        'bg-gradient-to-br from-background/50 via-background to-background/50',
        locale === 'ar' && 'direction-rtl'
      )}
    >
      {/* Mobile App-like Header Section */}
      <div className="mb-2">
        <div className="flex items-center justify-between py-3 md:py-4">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: locale === 'ar' ? 'الرئيسية' : 'Home', href: '/dashboard' },
              { label: locale === 'ar' ? 'عرض الملف' : 'View Profile' },
            ]}
          />

          {/* Mobile-Optimized Action Buttons */}
          <div className="flex items-center gap-1 md:gap-2 min-w-0 overflow-hidden">
            <RefreshBar className="flex-shrink-0 min-w-0" />
          </div>
        </div>
      </div>

      {/* Mobile App-like Student Profile Header */}
      <div className="relative mb-6 md:mb-8 space-y-4">
        <StudentProfileCard student={student} displayName={displayName} sourcedId={sourcedId} />

        {/* Parent Actions Banner - Full Width, Simple & Direct */}
        <Card
          className={clsx(
            'border-0 shadow-lg overflow-hidden touch-manipulation',
            'bg-gradient-to-r from-primary via-primary/95 to-primary/90',
            student.isActive && 'hover:shadow-xl transition-all duration-300'
          )}
        >
          <div className="relative px-4 py-4 md:px-8 md:py-5">
            {student.isActive ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="p-2.5 md:p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg">
                    <FileTextIcon color="white" />
                  </div>
                  <div>
                    <h3 className="text-sm md:text-base font-bold text-white flex items-center gap-2">
                      {locale === 'ar' ? 'إجراءات ولي الأمر' : 'Parent Actions'}
                    </h3>
                    <p className="text-xs md:text-sm text-white/90 mt-0.5">
                      {locale === 'ar'
                        ? 'الإجراءات المتاحة لإدارة سجل طفلك'
                        : "Available actions to manage your child's record"}
                    </p>
                  </div>
                </div>

                <div className="w-full sm:w-auto">
                  <StudentActions
                    studentId={student.id}
                    studentNumber={student.studentNumber}
                    isActive={student.isActive && !isPrivateEducation}
                    variant="white"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-3 text-white/90">
                <WarningCircleIcon />
                <p className="text-xs md:text-sm font-medium">
                  {locale === 'ar'
                    ? 'الإجراءات متاحة فقط للطلاب ذوي التسجيل النشط'
                    : 'Actions available only for students with active enrollment'}
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Student Information Section */}
      <Tabs defaultValue="info" className={clsx('w-full', locale === 'ar' && 'direction-rtl')}>
        {/* Student Information */}
        <TabsContent
          value="info"
          className={clsx('animate-in fade-in-50 duration-300', locale === 'ar' && 'direction-rtl')}
        >
          <div className="rounded-xl overflow-hidden touch-manipulation">
            <InfoTab person={student} t={t} locale={locale} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
